import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Cloud,
  Settings,
  History,
  Check,
  X,
  AlertCircle,
  Loader2,
  Save,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const Integrations = () => {
  const [integrations, setIntegrations] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  const [sfConfig, setSfConfig] = useState({
    auth_type: "token",
    username: "",
    password: "",
    security_token: "",
    client_id: "",
    client_secret: "",
    instance_url: "",
    object_type: "Opportunity",
    external_id_field: "External_ID__c",
    status_field: "StageName"
  });
  const [sfEnabled, setSfEnabled] = useState(false);
  
  const [logsPagination, setLogsPagination] = useState({ page: 1, limit: 20, total: 0 });

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API_URL}/integrations`);
      setIntegrations(response.data);
      
      const sf = response.data.find(i => i.name === "salesforce");
      if (sf) {
        setSfEnabled(sf.enabled);
        setSfConfig(prev => ({
          ...prev,
          ...sf.config,
          password: "",
          security_token: "",
          client_secret: ""
        }));
      }
    } catch (error) {
      console.error("Failed to fetch integrations");
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/integrations/logs?page=${logsPagination.page}&limit=${logsPagination.limit}`);
      setLogs(response.data.data);
      setLogsPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      console.error("Failed to fetch logs");
    }
  };

  useEffect(() => {
    fetchIntegrations();
    fetchLogs();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [logsPagination.page]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/integrations`, {
        name: "salesforce",
        enabled: sfEnabled,
        config: sfConfig
      });
      toast.success("Configurare salvată!");
      fetchIntegrations();
    } catch (error) {
      toast.error("Eroare la salvarea configurării");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (enabled) => {
    setSfEnabled(enabled);
    try {
      await axios.put(`${API_URL}/integrations/salesforce/toggle?enabled=${enabled}`);
      toast.success(enabled ? "Integrare activată" : "Integrare dezactivată");
    } catch (error) {
      toast.error("Eroare la actualizarea stării");
      setSfEnabled(!enabled);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/integrations/salesforce/sync`);
      toast.success(response.data.message);
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la sincronizare");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Integrări</h1>
        <p className="text-slate-500 mt-1">
          Configurează integrările cu sisteme externe
        </p>
      </div>

      <Tabs defaultValue="salesforce" className="w-full">
        <TabsList>
          <TabsTrigger value="salesforce" data-testid="tab-salesforce">
            <Cloud className="h-4 w-4 mr-2" />
            Salesforce
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">
            <History className="h-4 w-4 mr-2" />
            Istoric Sincronizări
          </TabsTrigger>
        </TabsList>

        <TabsContent value="salesforce" className="mt-6 space-y-6">
          {/* Status Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-sky-100 flex items-center justify-center">
                    <Cloud className="h-6 w-6 text-sky-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Salesforce Integration</h3>
                    <p className="text-sm text-slate-500">
                      Sincronizează statusul contractelor cu Salesforce
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Badge className={sfEnabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}>
                    {sfEnabled ? "Activ" : "Inactiv"}
                  </Badge>
                  <Switch
                    checked={sfEnabled}
                    onCheckedChange={handleToggleEnabled}
                    data-testid="sf-toggle"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Configurare Salesforce</CardTitle>
              <CardDescription>
                Setările de conectare și mapare câmpuri
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Auth Type */}
              <div className="space-y-2">
                <Label>Tip Autentificare</Label>
                <Select
                  value={sfConfig.auth_type}
                  onValueChange={(value) => setSfConfig({ ...sfConfig, auth_type: value })}
                >
                  <SelectTrigger data-testid="auth-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="token">Username + Security Token</SelectItem>
                    <SelectItem value="oauth">OAuth 2.0</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Credentials */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Instance URL</Label>
                  <Input
                    placeholder="https://yourorg.salesforce.com"
                    value={sfConfig.instance_url}
                    onChange={(e) => setSfConfig({ ...sfConfig, instance_url: e.target.value })}
                    data-testid="instance-url"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Username</Label>
                  <Input
                    placeholder="user@company.com"
                    value={sfConfig.username}
                    onChange={(e) => setSfConfig({ ...sfConfig, username: e.target.value })}
                    data-testid="sf-username"
                  />
                </div>

                {sfConfig.auth_type === "token" ? (
                  <>
                    <div className="space-y-2">
                      <Label>Parolă</Label>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={sfConfig.password}
                        onChange={(e) => setSfConfig({ ...sfConfig, password: e.target.value })}
                        data-testid="sf-password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Security Token</Label>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={sfConfig.security_token}
                        onChange={(e) => setSfConfig({ ...sfConfig, security_token: e.target.value })}
                        data-testid="sf-token"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Client ID</Label>
                      <Input
                        placeholder="Consumer Key"
                        value={sfConfig.client_id}
                        onChange={(e) => setSfConfig({ ...sfConfig, client_id: e.target.value })}
                        data-testid="client-id"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Client Secret</Label>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={sfConfig.client_secret}
                        onChange={(e) => setSfConfig({ ...sfConfig, client_secret: e.target.value })}
                        data-testid="client-secret"
                      />
                    </div>
                  </>
                )}
              </div>

              <Separator />

              {/* Field Mapping */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900">Mapare Câmpuri</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Object Type</Label>
                    <Input
                      placeholder="Opportunity"
                      value={sfConfig.object_type}
                      onChange={(e) => setSfConfig({ ...sfConfig, object_type: e.target.value })}
                      data-testid="object-type"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>External ID Field</Label>
                    <Input
                      placeholder="External_ID__c"
                      value={sfConfig.external_id_field}
                      onChange={(e) => setSfConfig({ ...sfConfig, external_id_field: e.target.value })}
                      data-testid="external-id-field"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Status Field</Label>
                    <Input
                      placeholder="StageName"
                      value={sfConfig.status_field}
                      onChange={(e) => setSfConfig({ ...sfConfig, status_field: e.target.value })}
                      data-testid="status-field"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleSyncNow}
                  disabled={syncing || !sfEnabled}
                  data-testid="sync-now"
                >
                  {syncing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Sincronizează Acum
                </Button>
                
                <Button onClick={handleSaveConfig} disabled={saving} data-testid="save-config">
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Salvează Configurare
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Info */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800">Notă Importantă</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Pentru a activa sincronizarea reală cu Salesforce, vă rugăm să furnizați 
                    credențialele corecte. Momentan, sistemul este configurat pentru simulare.
                    Asigurați-vă că aveți API access activat în Salesforce și că utilizatorul
                    are permisiunile necesare.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Istoric Sincronizări</CardTitle>
              <CardDescription>
                Loguri ale operațiunilor de sincronizare
              </CardDescription>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-12">
                  <History className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Nu există loguri de sincronizare</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data/Ora</TableHead>
                        <TableHead>Integrare</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Contracte</TableHead>
                        <TableHead>Detalii</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleString("ro-RO")}
                          </TableCell>
                          <TableCell>{log.integration_name}</TableCell>
                          <TableCell>
                            <Badge className={
                              log.status === "success" ? "bg-emerald-100 text-emerald-700" :
                              log.status === "failed" ? "bg-red-100 text-red-700" :
                              "bg-amber-100 text-amber-700"
                            }>
                              {log.status === "success" ? <Check className="h-3 w-3 mr-1" /> : 
                               log.status === "failed" ? <X className="h-3 w-3 mr-1" /> : null}
                              {log.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{log.contracts_count || 0}</TableCell>
                          <TableCell className="text-sm text-slate-500 max-w-xs truncate">
                            {log.details?.error || "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  {logsPagination.total > logsPagination.limit && (
                    <div className="flex items-center justify-between pt-4">
                      <p className="text-sm text-slate-500">
                        Total: {logsPagination.total} înregistrări
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={logsPagination.page === 1}
                          onClick={() => setLogsPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm">Pagina {logsPagination.page}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={logsPagination.page * logsPagination.limit >= logsPagination.total}
                          onClick={() => setLogsPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Integrations;
